import React, {useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import toast from 'react-hot-toast';
import {AiFillDelete, AiFillEdit} from 'react-icons/ai';

import CustomTable from '../components/common/CustomTable';
import InputField from '../components/ui/InputField';
import {invalidateQueryV2} from '../services/reactQuery';
import AlertModal from '../components/AlertModal';
import Button from '../components/ui/Button';

import {skillType} from '../utils/constants';
import AddSkillModal from '../components/Test/AddSkillModal';
import {useDeleteSkillMutation, useGetSkillsQuery} from '../api/candidate';

const ManageSkills = () => {
  const [formData, setFormData] = useState({
    sort: null,
    sortBy: null,
    page: 1,
    limit: 10,
    search: '',
  });
  const [skillModal, setSkillModal] = useState(false);
  const [deleteSkill, setDeleteSkill] = useState(null);

  const {mutate: removeDeleteSkillMutation, isLoading: isDeleteSkillLoading} =
    useDeleteSkillMutation();

  const {
    data: skillData,
    isFetching: isSkillLoading,
    refetch,
  } = useGetSkillsQuery();

  const allSkills = skillData?.data || [];

  const totalItems = allSkills.length;
  const totalPages = totalItems / formData.limit;

  const memoisedSkills = useMemo(() => {
    let filteredData = allSkills.filter(({name}) => {
      return name.toLowerCase().includes(formData.search);
    });

    const startRange = formData.page * formData.limit - formData.limit;
    filteredData = filteredData.splice(startRange, formData.limit);
    return filteredData;
  }, [allSkills, formData]);

  // form management
  const {handleSubmit, watch, register} = useForm({
    mode: 'all',
  });

  const handleFormSubmission = async (values) => {
    const {search} = values;
    let payload = {search: search.toLowerCase()};
    // set pagination to page 1
    setFormData((prev) => ({
      ...prev,
      ...payload,
      page: 1,
    }));
  };

  useEffect(() => {
    const subscription = watch(handleSubmit(handleFormSubmission));
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  const handleDeleteSkill = (skillId) => {
    removeDeleteSkillMutation(skillId, {
      onSuccess: () => {
        setDeleteSkill(null);
        toast.success('Skill successfully removed.');
        refetch();
      },
      onError: (data) =>
        toast.error(data.response.data.message || 'something went wrong'),
    });
  };

  const handleSort = (sortingDetails) => {
    const {page = null, sortBy, sortDirection = null} = sortingDetails;
    setFormData((prevFormData) => ({
      ...prevFormData,
      page: page || prevFormData.page,
      sortBy,
      sort: sortDirection,
    }));
  };

  const tHeadData = [
    {
      id: 1,
      keyIndex: 'name',
      label: 'Skill',
      sort: true,
      width: '33.33%',
    },
    {
      id: 2,
      keyIndex: 'type',
      label: 'Type',
      width: '33.33%',
      render: (item) => skillType[item] || '-',
    },
    {
      id: 7,
      keyIndex: '_id',
      label: 'Action',
      width: '33.33%',
      render: (_id, itemDetails) => (
        <div className="flex gap-2">
          <button onClick={() => setSkillModal(itemDetails)}>
            <AiFillEdit size={20} />
          </button>
          <button
            type="button"
            aria-label="remove candidate from job"
            onClick={() => setDeleteSkill(_id)}
          >
            <AiFillDelete size={20} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      {skillModal && (
        <AddSkillModal
          isOpen={skillModal}
          onClose={() => setSkillModal(false)}
          title={
            typeof skillModal === 'object'
              ? `Update skill - ${skillModal.name}`
              : 'Add skill'
          }
          skillDetails={skillModal}
          width="lg"
          editMode={typeof skillModal === 'object'}
          refetch={refetch}
        />
      )}

      <AlertModal
        type="warning"
        isOpen={deleteSkill}
        onClose={() => setDeleteSkill(null)}
        title="Confirm this action"
        onCancel={() => setDeleteSkill(null)}
        onOK={() => handleDeleteSkill(deleteSkill)}
        okLoading={isDeleteSkillLoading}
      >
        Are you sure you want to delete this skill?
      </AlertModal>

      <div className="px-8 py-6">
        <div className="flex gap-2">
          <h3 className="text-2xl font-bold">
            Skills {!!Number(totalItems) && `(${totalItems})`}
          </h3>
        </div>
        <div className="w-full">
          {/* <div className="w-full max-w-[1100px]"> */}
          <form className="py-5">
            <div className="flex justify-between">
              <div className="flex gap-4">
                <InputField
                  {...register('search')}
                  type="search"
                  placeholder="Search"
                  className="w-60"
                />
              </div>

              <div className="flex items-center gap-3">
                <Button
                  className="py-3 px-5"
                  onClick={() => setSkillModal(true)}
                >
                  Add Skills
                </Button>
              </div>
            </div>
          </form>

          <div className="bg-[white] p-10 border">
            <CustomTable
              tHeadData={tHeadData}
              tBodyData={memoisedSkills}
              handleSort={handleSort}
              pageCount={totalPages}
              currentPage={formData.page}
              loading={isSkillLoading}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageSkills;
